import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { useClub } from '../contexts/ClubContext';

function Navbar() {
  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { club, clubId } = useClub();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // Try to get user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            // Set user role for admin link visibility
            setUserRole(data.role);
            // Use firstName + lastName if available
            if (data.firstName && data.lastName) {
              setDisplayName(`${data.firstName} ${data.lastName}`);
            } else if (data.displayName) {
              setDisplayName(data.displayName);
            } else {
              // Fallback to email prefix
              setDisplayName(currentUser.email.split('@')[0]);
            }
          } else {
            // No user doc yet - just set display name, don't create user doc here
            // User docs should be created via Setup, CreateClub, or Landing page signup
            const emailName = currentUser.email.split('@')[0];
            setDisplayName(emailName);
            setUserRole(null);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setDisplayName(currentUser.email.split('@')[0]);
        }
      } else {
        setDisplayName('');
        setUserRole(null);
      }
    });
    return () => unsubscribe();
  }, [clubId]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="navbar">
      <Link to="/" className="nav-logo" onClick={closeMobileMenu}>
        <span className="logo-icon">⚽</span>
        <span className="logo-text">CLUBLINK TT</span>
      </Link>

      {/* Hamburger Menu Button */}
      <button 
        className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>

      {/* Navigation Links */}
      <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <Link to="/" className="nav-link" onClick={closeMobileMenu}>Home</Link>
        <Link to="/register" className="nav-link" onClick={closeMobileMenu}>Register</Link>
        <Link to="/teams" className="nav-link" onClick={closeMobileMenu}>Teams</Link>
        <Link to="/schedule" className="nav-link" onClick={closeMobileMenu}>Schedule</Link>
        <Link to="/kitshop" className="nav-link" onClick={closeMobileMenu}>Kit Shop</Link>
        <Link to="/gallery" className="nav-link" onClick={closeMobileMenu}>Gallery</Link>
        <Link to="/contact" className="nav-link" onClick={closeMobileMenu}>Contact</Link>
        
        {/* Mobile Auth Section */}
        <div className="nav-auth-mobile">
          {user ? (
            <>
              <span className="nav-welcome-mobile">Welcome, {displayName}</span>
              <Link to={`/dashboard?club=${clubId}`} className="nav-link" onClick={closeMobileMenu}>My Dashboard</Link>
              {userRole === 'coach' && (
                <Link to={`/coach?club=${clubId}`} className="nav-link" onClick={closeMobileMenu}>Coach Dashboard</Link>
              )}
              {(userRole === 'club_admin' || userRole === 'super_admin') && (
                <a href={`/admin/dashboard?club=${clubId}`} className="nav-link" onClick={closeMobileMenu}>Admin Dashboard</a>
              )}
              {userRole === 'super_admin' && (
                <Link to="/super-admin" className="nav-link" onClick={closeMobileMenu}>Super Admin</Link>
              )}
              <button onClick={handleLogout} className="nav-logout-btn-mobile">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link" onClick={closeMobileMenu}>Login</Link>
              <Link to="/register" className="nav-signup-btn-mobile" onClick={closeMobileMenu}>Sign Up</Link>
            </>
          )}
        </div>
      </div>

      {/* Desktop Auth Section */}
      <div className="nav-auth">
        {user ? (
          <div className="nav-user">
            <span className="nav-welcome">Welcome, {displayName}</span>
            <Link to={`/dashboard?club=${clubId}`} className="nav-dashboard-btn">My Dashboard</Link>
            {userRole === 'coach' && (
              <Link to={`/coach?club=${clubId}`} className="nav-coach-btn">Coach</Link>
            )}
            {(userRole === 'club_admin' || userRole === 'super_admin') && (
              <a href={`/admin/dashboard?club=${clubId}`} className="nav-admin-btn">Admin</a>
            )}
            {userRole === 'super_admin' && (
              <Link to="/super-admin" className="nav-super-admin-btn">Super Admin</Link>
            )}
            <button onClick={handleLogout} className="nav-logout-btn">Logout</button>
          </div>
        ) : (
          <>
            <Link to="/login" className="nav-login-btn">Login</Link>
            <Link to="/register" className="nav-signup-btn">Sign Up</Link>
          </>
        )}
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>}
    </nav>
  );
}

export default Navbar;
